import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Edit, 
  Trash2, 
  Heart,
  Shield,
  Swords,
  Zap,
  Star,
  Sparkles,
  ExternalLink,
  Copy,
  Check
} from 'lucide-react';
import { getPokemonImageUrl, getPokemonImageSrcList } from '../../../utils/imageUtils';

const PokemonDetailModal = ({ isOpen, onClose, pokemon, onEdit, onDelete }) => {
  const [copied, setCopied] = useState(false);

  const getTypeColor = (type) => {
    const typeColors = {
      'Fire': 'bg-red-500',
      'Water': 'bg-blue-500',
      'Grass': 'bg-green-500',
      'Electric': 'bg-yellow-500',
      'Psychic': 'bg-purple-500',
      'Ice': 'bg-cyan-500',
      'Dragon': 'bg-indigo-500',
      'Dark': 'bg-gray-800',
      'Fairy': 'bg-pink-500',
      'Fighting': 'bg-orange-500',
      'Poison': 'bg-violet-500',
      'Ground': 'bg-amber-600',
      'Flying': 'bg-sky-500',
      'Bug': 'bg-lime-500',
      'Rock': 'bg-stone-500',
      'Ghost': 'bg-purple-600',
      'Steel': 'bg-gray-500',
      'Normal': 'bg-gray-400'
    };
    return typeColors[type] || 'bg-gray-400';
  };

  const formatTypes = (typesString) => {
    if (!typesString) return [];
    return typesString.split(',').map(type => type.trim());
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (!isOpen || !pokemon) return null;

  const types = formatTypes(pokemon.types);
  const totalStats = (pokemon.HP || 0) + (pokemon.Attack || 0) + (pokemon.Defense || 0);
  const isLegendary = pokemon.Max_CP_Lv50 > 4000;
  const isMythical = pokemon.Max_CP_Lv50 > 5000;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-2xl max-h-[95vh] overflow-hidden flex flex-col mx-2 sm:mx-0"
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '95vh' }}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700 bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                <ExternalLink className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">รายละเอียดโปเกมอน</h2>
                <p className="text-indigo-100 text-sm">ข้อมูลครบถ้วนของโปเกมอน</p>
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => onEdit && onEdit(pokemon)}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                title="แก้ไข"
              >
                <Edit className="w-5 h-5" />
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 overflow-y-auto flex-1 min-h-0">
            <div className="space-y-6">
              {/* Pokemon Image and Basic Info */}
              <div className="flex items-start space-x-6">
                {/* Image */}
                <div className="relative w-40 h-40 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-700 dark:to-gray-800 rounded-2xl flex items-center justify-center shadow-lg flex-shrink-0">
                  <img
                    src={getPokemonImageUrl(pokemon.Pokemon_ID)}
                    alt={pokemon.Pokemon_Name_EN}
                    className="w-full h-full object-contain p-4"
                    onError={(e) => {
                      const sources = getPokemonImageSrcList(pokemon.Pokemon_ID);
                      const currentIndex = sources.indexOf(e.target.src);
                      if (currentIndex < sources.length - 1) {
                        e.target.src = sources[currentIndex + 1];
                      }
                    }}
                  />
                  {(isLegendary || isMythical) && (
                    <div className="absolute -top-2 -right-2">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        isMythical ? 'bg-purple-500' : 'bg-yellow-500'
                      }`}>
                        {isMythical ? (
                          <Sparkles className="w-5 h-5 text-white" />
                        ) : (
                          <Star className="w-5 h-5 text-white" />
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Basic Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center space-x-3 mb-3">
                    <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
                      {pokemon.Pokemon_Name_EN}
                    </h1>
                    {(isLegendary || isMythical) && (
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        isMythical ? 'bg-purple-500' : 'bg-yellow-500'
                      }`}>
                        {isMythical ? (
                          <Sparkles className="w-4 h-4 text-white" />
                        ) : (
                          <Star className="w-4 h-4 text-white" />
                        )}
                      </div>
                    )}
                  </div>
                  
                  <h2 className="text-2xl text-gray-600 dark:text-gray-400 mb-2">
                    {pokemon.Pokemon_Name_TH}
                  </h2>
                  
                  <div className="flex items-center space-x-2 mb-4">
                    <span className="text-lg text-gray-500 dark:text-gray-500 font-mono">
                      #{pokemon.Pokemon_ID}
                    </span>
                    <button
                      onClick={() => copyToClipboard(pokemon.Pokemon_ID)}
                      className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                      title="คัดลอก ID"
                    >
                      {copied ? (
                        <Check className="w-4 h-4 text-green-600" />
                      ) : (
                        <Copy className="w-4 h-4 text-gray-400" />
                      )}
                    </button>
                  </div>

                  {/* Types */}
                  <div className="flex flex-wrap gap-2 mb-4">
                    {types.map((type, idx) => (
                      <span
                        key={idx}
                        className={`px-3 py-1 text-sm font-medium text-white rounded-full ${getTypeColor(type)}`}
                      >
                        {type}
                      </span>
                    ))}
                  </div>

                  {/* Max CP */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <Zap className="w-6 h-6 text-blue-500" />
                        <span className="text-lg font-medium text-gray-700 dark:text-gray-300">Max CP (Level 50)</span>
                      </div>
                      <span className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                        {pokemon.Max_CP_Lv50 ? pokemon.Max_CP_Lv50.toLocaleString() : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Stats Grid */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Heart className="w-5 h-5 text-red-500" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">HP</span>
                  </div>
                  <div className="text-2xl font-bold text-red-600 dark:text-red-400">
                    {pokemon.HP || 'N/A'}
                  </div>
                </div>

                <div className="bg-orange-50 dark:bg-orange-900/20 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Swords className="w-5 h-5 text-orange-500" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">Attack</span>
                  </div>
                  <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                    {pokemon.Attack || 'N/A'}
                  </div>
                </div>

                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4 text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Shield className="w-5 h-5 text-blue-500" />
                    <span className="font-medium text-gray-700 dark:text-gray-300">Defense</span>
                  </div>
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {pokemon.Defense || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Total Stats */}
              <div className="bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 rounded-xl p-6 text-center">
                <div className="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Total Stats
                </div>
                <div className="text-4xl font-bold text-green-600 dark:text-green-400">
                  {totalStats.toLocaleString()}
                </div>
              </div>

              {/* Additional Info */}
              {pokemon.Gigantamax_Move && (
                <div className="bg-purple-50 dark:bg-purple-900/20 rounded-xl p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Gigantamax Move
                  </h3>
                  <p className="text-purple-600 dark:text-purple-400 font-medium">
                    {pokemon.Gigantamax_Move}
                  </p>
                </div>
              )}

              {/* Legendary/Mythical Info */}
              {(isLegendary || isMythical) && (
                <div className={`${isMythical ? 'bg-purple-50 dark:bg-purple-900/20' : 'bg-yellow-50 dark:bg-yellow-900/20'} rounded-xl p-4`}>
                  <div className="flex items-center space-x-2 mb-2">
                    {isMythical ? (
                      <Sparkles className="w-5 h-5 text-purple-500" />
                    ) : (
                      <Star className="w-5 h-5 text-yellow-500" />
                    )}
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {isMythical ? 'Mythical Pokemon' : 'Legendary Pokemon'}
                    </h3>
                  </div>
                  <p className={`${isMythical ? 'text-purple-600 dark:text-purple-400' : 'text-yellow-600 dark:text-yellow-400'}`}>
                    {isMythical 
                      ? 'โปเกมอนในตำนานที่หายากมากและมีพลังสูงสุด' 
                      : 'โปเกมอนในตำนานที่มีพลังสูงและหายาก'
                    }
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 p-4 sm:p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50 flex-shrink-0">
            <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-3">
              <button
                onClick={() => onEdit && onEdit(pokemon)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Edit className="w-4 h-4" />
                <span>แก้ไข</span>
              </button>
              <button
                onClick={() => onDelete && onDelete(pokemon.Pokemon_ID)}
                className="flex items-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="w-4 h-4" />
                <span>ลบ</span>
              </button>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors"
            >
              ปิด
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PokemonDetailModal;
